/**
 * tRPC Client Setup
 */

import type { AppRouter } from '@polygraf/server/trpc'
import {
	getNativeApiUrl,
	getNativeToken,
	isPrivateNetworkHost,
	resolveApiBaseUrl,
} from '@polygraf/shared'
import { createTRPCReact, httpBatchLink } from '@trpc/react-query'
import superjson from 'superjson'

export const trpc = createTRPCReact<AppRouter>()

// Empty-string base means "relative URLs", which the Vite dev proxy + same-
// origin prod deployments both expect. The fetch wrapper below applies the
// native bridge separately when running inside a WebView so loopback origins
// get rewritten to the host's API URL.
const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL)

export const trpcClient = trpc.createClient({
	links: [
		httpBatchLink({
			url: `${API_URL}/api/trpc`,
			transformer: superjson,
			async fetch(url, options) {
				// In a native WebView, the page origin (e.g. file:// or a bundled
				// localhost server) is unreachable from the device — rewrite any
				// loopback / private host to the API URL injected by the host.
				let resolvedUrl: string | URL = url instanceof URL ? url : url.toString()
				const nativeApiUrl = getNativeApiUrl()
				if (nativeApiUrl) {
					const urlStr = url.toString()
					if (urlStr.startsWith('/')) {
						// Bare path → prefix with the native API URL.
						resolvedUrl = `${nativeApiUrl}${urlStr}`
					} else {
						try {
							const parsed = new URL(urlStr)
							// Locality detection lives in @polygraf/shared/network-allowlist.
							if (isPrivateNetworkHost(parsed.hostname)) {
								// Replace origin with native API origin, keep path/search/hash.
								const nativeOrigin = new URL(nativeApiUrl)
								parsed.protocol = nativeOrigin.protocol
								parsed.host = nativeOrigin.host
								resolvedUrl = parsed.toString()
							}
						} catch {
							// Non-URL string — leave as-is.
						}
					}
				}

				// In a native WebView the SSO session lives in SecureStore, not a
				// cookie the WebView holds, so `credentials: 'include'` sends nothing
				// and non-PUBLIC sources 401. The shell injects the session token via
				// the bridge; forward it as a bearer so authorizeSpaceAccess's bearer
				// fallback authorizes the call (M1). No-op outside a WebView.
				const nativeToken = getNativeToken()
				const headers = new Headers(options?.headers)
				if (nativeToken) {
					headers.set('Authorization', `Bearer ${nativeToken}`)
				}

				const response = await fetch(resolvedUrl, {
					...options,
					headers,
					credentials: 'include',
				})

				return response
			},
		}),
	],
})
